import { keepPreviousData, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import _ from 'lodash'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import { useRef, useState } from 'react'
import { useParams } from 'react-router'

import { DataTable } from '@/components/DataTable'
import { ColumnHeader, SortableColumnHeader } from '@/components/DataTable/DataTable'
import { Button } from '@/components/elements/button'
import { Input } from '@/components/elements/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/elements/tooltip'
import { Layout } from '@/components/Layout'
import { MultiSelect } from '@/components/MultiSelect'
import { TextLink } from '@/components/TextLink'
import { getAllEntityCertificates } from '@/services/entity_certificates.service'
import { getStudents } from '@/services/entity_students.service'
import { hasAnyFilterSelected } from '@/utils/helpers'
import { formatUserName } from '@/utils/userHelpers'

const DEFAULT_FILTERS = {
  certificate: [],
}

const CertificatesList = ({ certificates = [] }) => {
  if (certificates.length <= 2) {
    return (
      <span className="truncate">
        {certificates.map((cert) => cert.certificate.name).join(', ')}
      </span>
    )
  }

  const displayedCertificates = certificates.slice(0, 2)
  const remainingCount = certificates.length - 2
  const allCertificates = certificates.map((cert) => cert.certificate.name)

  return (
    <div className="flex items-center gap-2">
      <span className="truncate">
        {displayedCertificates.map((cert) => cert.certificate.name).join(', ')}
        {remainingCount > 0 ? ', ' : ''}
      </span>

      <Tooltip>
        <TooltipTrigger className="bg-muted-foreground rounded-full px-2 py-1 font-mono text-xs text-white">
          +{remainingCount}
        </TooltipTrigger>
        <TooltipContent>
          <ul>
            {allCertificates.map((cert) => (
              <li key={cert}>{cert}</li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

CertificatesList.propTypes = {
  certificates: PropTypes.array,
}

const Students = observer(() => {
  const { entityId } = useParams()
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [sorting, setSorting] = useState([
    { id: 'user__last_name', desc: false },
    { id: 'user__first_name', desc: false },
  ])
  const [pagination, setPagination] = useState({
    pageSize: 20,
    pageIndex: 0,
  })

  const { data: students, isPending } = useQuery({
    queryKey: [
      'accounts',
      'instructors',
      'students',
      entityId,
      sorting,
      pagination,
      searchTerm,
      filters,
    ],
    queryFn: () =>
      getStudents({
        entityId,
        sorting,
        pagination,
        searchTerm,
        filters,
      }),
    placeholderData: keepPreviousData,
  })

  const { data: certificates } = useQuery({
    queryKey: ['accounts', 'instructors', 'certificates', entityId],
    queryFn: () => getAllEntityCertificates(entityId),
    placeholderData: keepPreviousData,
  })

  // Filter or search changes shrink the result set, so a deep page can 404
  // from the paginator. Reset to page 0 in the same handler so React batches
  // both updates into a single render.
  const resetToFirstPage = () =>
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }))

  const debounceSearch = useRef(
    _.debounce((value) => {
      setSearchTerm(value)
      resetToFirstPage()
    }, 500),
  ).current
  return (
    <Layout breadcrumbs={['Instructor', 'Students']}>
      <Layout.Heading title="Students" />

      {/* @fixme: This is a hacky way to make the table filters responsive */}
      <div className="flex flex-row items-center gap-2">
        {(searchTerm || hasAnyFilterSelected(filters)) && (
          <Button
            className="visible md:hidden"
            variant="outline"
            onClick={() => {
              setSearch('')
              setSearchTerm('')
              setFilters(DEFAULT_FILTERS)
              resetToFirstPage()
            }}
          >
            Clear Filters
          </Button>
        )}
      </div>

      <div className="flex w-full flex-row items-center justify-between">
        <div className="mt-2 mb-2 flex w-full flex-col items-center gap-4 py-1 md:flex-row">
          <Input
            className="w-full md:w-64"
            placeholder="Search for Student"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              debounceSearch(e.target.value)
            }}
          />

          <MultiSelect
            placeholder="Filter by Course"
            options={certificates?.map((c) => ({ label: c.name, id: c.id })) || []}
            onChange={(value) => {
              setFilters((prev) => ({
                ...prev,
                certificate: value || [],
              }))
              resetToFirstPage()
            }}
            value={filters.certificate}
            width="w-full md:w-56"
          />

          {(searchTerm || hasAnyFilterSelected(filters)) && (
            <Button
              className="hidden md:block"
              variant="outline"
              onClick={() => {
                setSearch('')
                setSearchTerm('')
                setFilters(DEFAULT_FILTERS)
                resetToFirstPage()
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        {/* <Button className="hidden md:block" onClick={() => setShowUserModal(true)}>
              Add User
            </Button> */}
      </div>

      <DataTable
        columns={[
          {
            accessorFn: (student) => formatUserName(student.user),
            id: 'user__last_name',
            header: ({ column }) => <SortableColumnHeader column={column} label="Name" />,
            cell: ({ row, getValue }) => (
              <TextLink to={`${row.original.id}`}>{getValue()}</TextLink>
            ),
          },
          {
            accessorKey: 'user__email',
            header: ({ column }) => <SortableColumnHeader column={column} label="Email" />,
            cell: ({ row }) => <span>{row.original.user.email}</span>,
          },
          {
            accessorKey: 'user__last_login',
            header: ({ column }) => <SortableColumnHeader column={column} label="Last Login" />,
            cell: ({ row }) => {
              if (!row.original.user.lastLogin) return 'N/A'
              return dayjs(row.original.user.lastLogin).format('MMM D, YYYY h:mm A')
            },
          },
          {
            accessorKey: 'user__last_activity',
            header: ({ column }) => <SortableColumnHeader column={column} label="Last Activity" />,
            cell: ({ row }) => {
              if (!row.original.user.lastActivity) return 'N/A'
              return dayjs(row.original.user.lastActivity).format('MMM D, YYYY h:mm A')
            },
          },
          {
            accessorKey: 'student_certificates',
            id: 'certificate',
            header: () => <ColumnHeader label="Certificates" />,
            cell: ({ row }) =>
              row.original.studentCertificates?.length > 0 ? (
                <CertificatesList certificates={row.original.studentCertificates} />
              ) : (
                <span>None</span>
              ),
          },
        ]}
        data={students?.results || []}
        loading={isPending}
        pagination={pagination}
        rowCount={students?.count || 0}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
      />
    </Layout>
  )
})

export default Students
